import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import confetti from 'canvas-confetti';
import LZString from 'lz-string';
import './ConnectionsGame.css';

interface Category {
  title: string;
  items: string[];
  color: string;
}

const LEVEL_COLORS = ['#f9df6d', '#a0c35a', '#b0c4ef', '#ba81c5'];

const DEFAULT_CATEGORIES: Category[] = [
  { title: "JAVASCRIPT ECOSYSTEM", items: ['REACT', 'TYPESCRIPT', 'NODE', 'FIREBASE'], color: LEVEL_COLORS[0] },
  { title: "FRUITS", items: ['APPLE', 'BANANA', 'CHERRY', 'DATE'], color: LEVEL_COLORS[1] },
  { title: "MUSICAL INSTRUMENTS", items: ['PIANO', 'GUITAR', 'DRUMS', 'FLUTE'], color: LEVEL_COLORS[2] },
  { title: "COLORS", items: ['RED', 'BLUE', 'GREEN', 'YELLOW'], color: LEVEL_COLORS[3] },
];

/**
 * Base64 encode/decode helpers
 * Format: Comma-separated list of 20 strings.
 * Order: Group 1 (Yellow) [4 items, title], Group 2 (Green) [4 items, title], etc.
 */
const encodeGame = (categories: Category[]): string => {
  try {
    const parts = categories.flatMap(cat => [...cat.items, cat.title]);
    const csv = parts.join(',');
    return LZString.compressToEncodedURIComponent(csv);
  } catch (e) {
    console.error("Failed to encode game", e);
    return "";
  }
};

const decodeGame = (str: string): Category[] | null => {
  try {
    const csv = LZString.decompressFromEncodedURIComponent(str);

    if (!csv) return null;

    const parts = csv.split(',');

    if (parts.length !== 20) return null;

    const categories: Category[] = [];
    for (let i = 0; i < 4; i++) {
      const chunk = parts.slice(i * 5, (i * 5) + 5);
      const items = chunk.slice(0, 4);
      const title = chunk[4];

      categories.push({
        title,
        items,
        color: LEVEL_COLORS[i]
      });
    }

    return categories;
  } catch (e) {
    console.error("Failed to decode game", e);
    return null;
  }
};

const getInitialGameData = (): { categories: Category[], error: string | null } => {
  const params = new URLSearchParams(window.location.search);
  const puzzleHash = params.get('puzzle');
  if (puzzleHash) {
    const decoded = decodeGame(puzzleHash);
    if (decoded) return { categories: decoded, error: null };
    return { categories: [], error: "That puzzle code didn't seem to work 😢" };
  }
  return { categories: [], error: null };
};

// Helper: Create tiles from categories
const generateTiles = (categories: Category[]): Tile[] => {
  return categories.flatMap(cat => cat.items).map((text, index) => ({
    id: index + 1,
    text
  }));
};

const shuffleArray = (array: Tile[]) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const FitText: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;

    const resizeObserver = new ResizeObserver(() => {
      const parentWidth = container.clientWidth;
      const childWidth = textEl.scrollWidth;

      if (childWidth > parentWidth) {
        setScale(parentWidth / childWidth);
      } else {
        setScale(1);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [children]);

  return (
    <div ref={containerRef} style={{ width: '100%', padding: '0 12px', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <span ref={textRef} style={{
        whiteSpace: 'nowrap',
        transform: `scale(${scale})`,
        transformOrigin: 'center',
        display: 'inline-block'
      }}>
        {children}
      </span>
    </div>
  );
};

const ConnectionsGame: React.FC = () => {
  // Initialize categories (either from URL or Default)
  const [{ categories, error }] = useState(getInitialGameData);

  // Initialize tiles based on the categories
  const [tiles, setTiles] = useState<Tile[]>(() => {
    if (error) return [];
    return shuffleArray(generateTiles(categories));
  });

  const [isCreatorMode, setIsCreatorMode] = useState<boolean>(categories.length === 0);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [solvedCategories, setSolvedCategories] = useState<Category[]>([]);
  const [animatingIds, setAnimatingIds] = useState<number[]>([]);
  const [animationPhase, setAnimationPhase] = useState<'idle' | 'bounce' | 'fly-up' | 'shake'>('idle');
  const [floatingCategory, setFloatingCategory] = useState<{ category: Category; rect: DOMRect | null } | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackExiting, setFeedbackExiting] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string>("");
  const [debugInput, setDebugInput] = useState<string>("");
  const [debugOutput, setDebugOutput] = useState<string>("");

  const tileRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const solvedListRef = useRef<HTMLDivElement>(null);
  const prevRects = useRef<Map<number, DOMRect>>(new Map());

  useEffect(() => {
    if (categories.length > 0 && solvedCategories.length === categories.length) {
      // Small delay to let the last bar animation finish
      setTimeout(() => {
        confetti({
          particleCount: 200,
          spread: 90,
          origin: { y: 0.35 }
        });
      }, 500);
    }
  }, [solvedCategories, categories.length]);

  useLayoutEffect(() => {
    // Layout shuffle logic (existing)
    if (animationPhase === 'idle') {
      tileRefs.current.forEach((el, id) => {
        const prevRect = prevRects.current.get(id);
        if (prevRect) {
          const newRect = el.getBoundingClientRect();
          const dx = prevRect.left - newRect.left;
          const dy = prevRect.top - newRect.top;

          if (dx !== 0 || dy !== 0) {
            el.style.transform = `translate(${dx}px, ${dy}px)`;
            el.style.transition = 'none';

            requestAnimationFrame(() => {
              el.style.transform = ''; // Clear for future interactions
              el.style.transition = 'transform 300ms ease';
            });
          }
        }
      });
    }

    // Fly-up animation logic
    if (animationPhase === 'fly-up' && solvedListRef.current) {
      const solvedRect = solvedListRef.current.getBoundingClientRect();

      // Target is either the floating category position or calculated bottom
      // Since we are showing a floating category, we should target THAT.
      // But for simplicity, we target the space where it will be.

      let targetY = solvedRect.bottom;
      if (solvedCategories.length > 0) targetY += 10;

      const targetX = solvedRect.left + (solvedRect.width / 2);

      animatingIds.forEach(id => {
        const el = tileRefs.current.get(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          // Calculate center of tile
          const currentX = rect.left + (rect.width / 2);
          const currentY = rect.top + (rect.height / 2);

          const deltaX = targetX - currentX;
          const deltaY = targetY + 20 - currentY; // +20 roughly centers in the new row height (approx 60-80px?)

          // We need to force a reflow or simply apply style for transition
          el.style.transition = 'all 500ms ease-in-out';
          el.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(0.5)`;
          el.style.opacity = '0';
        }
      });
    }
  }, [tiles, animationPhase, animatingIds, solvedCategories.length]);

  const handleShuffle = () => {
    // Capture current positions
    tileRefs.current.forEach((el, id) => {
      prevRects.current.set(id, el.getBoundingClientRect());
    });

    setTiles(prev => shuffleArray(prev));
  };

  const handleSubmit = () => {
    if (selectedIds.length !== 4) return;

    const selectedTiles = tiles.filter(t => selectedIds.includes(t.id));
    const firstTileText = selectedTiles[0].text;

    // Find category that contains this tile
    const category = categories.find(cat => cat.items.includes(firstTileText));

    if (category) {
      // Check if all selected tiles belong to this category
      const isMatch = selectedTiles.every(t => category.items.includes(t.text));

      if (isMatch) {
        setAnimatingIds(selectedIds);
        setAnimationPhase('bounce');

        // Wait for bounce animation to finish
        setTimeout(() => {
          // Prepare floating category
          if (solvedListRef.current) {
            const rect = solvedListRef.current.getBoundingClientRect();
            setFloatingCategory({ category, rect });
          }

          setAnimationPhase('fly-up');

          // Wait for fly-up animation to finish, then update state
          setTimeout(() => {
            setSolvedCategories(prev => [...prev, category]);
            setTiles(tiles.filter(t => !selectedIds.includes(t.id)));
            setSelectedIds([]);
            setAnimatingIds([]);
            setAnimationPhase('idle');
            setFloatingCategory(null);
          }, 500);

        }, 1000); // 500ms bounce + max 300ms staggering delay
      } else {
        // Validation logic for "One Away"
        const selectedTiles = tiles.filter(t => selectedIds.includes(t.id));
        const selectedTexts = selectedTiles.map(t => t.text);

        let isOneAway = false;

        // check each category
        for (const cat of categories) {
          const matches = cat.items.filter(item => selectedTexts.includes(item));
          if (matches.length === 3) {
            isOneAway = true;
            break;
          }
        }

        setAnimatingIds(selectedIds);
        setAnimationPhase('shake');
        if (isOneAway) {
          setFeedbackMessage("One away...");
          setFeedbackExiting(false);
        }

        setTimeout(() => {
          setAnimatingIds([]);
          setAnimationPhase('idle');

          // Allow message to linger if it exists
          if (isOneAway) {
            setTimeout(() => {
              setFeedbackExiting(true);
              // Wait for fade out animation
              setTimeout(() => {
                setFeedbackMessage(null);
                setFeedbackExiting(false);
              }, 300);
            }, 1000);
          } else {
            setFeedbackMessage(null); // Should be null anyway, but safe cleanup
          }
        }, 600);
      }
    }
  };

  const toggleTile = (id: number) => {
    if (animatingIds.length > 0) return; // Block interaction during animation
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else if (selectedIds.length < 4) {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const deselectAll = () => setSelectedIds([]);

  // Creator State
  const [creatorCategories, setCreatorCategories] = useState<Category[]>(() => {
    // Initialize with default categories for easier testing
    return DEFAULT_CATEGORIES.map(cat => ({
      title: cat.title,
      items: [...cat.items],
      color: cat.color
    }));
  });

  const handleCreatorChange = (catIndex: number, itemIndex: number, value: string) => {
    const newCats = [...creatorCategories];
    if (itemIndex === -1) {
      // Title change
      newCats[catIndex].title = value.toUpperCase();
    } else {
      // Item change
      newCats[catIndex].items[itemIndex] = value.toUpperCase();
    }
    setCreatorCategories(newCats);
  };

  const handleCreateLink = () => {
    const encoded = encodeGame(creatorCategories);
    const url = `${window.location.origin}${window.location.pathname}?puzzle=${encoded}`;

    setGeneratedLink(encoded);

    // Copy to clipboard or alert
    navigator.clipboard.writeText(url).then(() => {
      alert("Copied game link to clipboard!");
    }).catch(err => {
      console.error('Failed to copy: ', err);
      alert("Failed to copy link. See console.");
    });
    // Log for debugging
    console.log("Created URL:", url);
  };

  const handleDebugInputChange = (value: string) => {
    setDebugInput(value);

    if (!value) {
      setDebugOutput("");
      return;
    }

    const decoded = LZString.decompressFromEncodedURIComponent(value);

    if (!decoded) {
      setDebugOutput("Invalid puzzle code");
      return;
    }

    setDebugOutput(decoded);
  };

  if (error) {
    return (
      <div className="game-container">
        <div className="error-container" style={{ textAlign: 'center', marginTop: '60px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '20px' }}>{error}</h2>
          <button
            className="game-btn"
            onClick={() => window.location.search = ''}
          >
            Go back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', width: '100%', borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
         <h1 style={{ fontFamily: 'Roboto Slab, serif', fontWeight: 700, margin: 0 }}>Conneckshuns</h1>
         {categories.length > 0 && (
             <button 
                className="game-btn" 
                style={{ padding: '8px 16px', fontSize: '0.9rem', borderRadius: '4px' }}
                onClick={() => setIsCreatorMode(!isCreatorMode)}
             >
                {isCreatorMode ? "Back to Puzzle" : "Create your own"}
             </button>
         )}
      </div>

      {categories.length > 0 && !isCreatorMode && (
        <>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 'normal', margin: '0 0 15px 0' }}>Group em together or whatever</h2>
          <div className="solved-categories" ref={solvedListRef}>
            {solvedCategories.map((cat) => (
              <div key={cat.title} className="solved-row" style={{ backgroundColor: cat.color }}>
                <h3>{cat.title}</h3>
                <p>{cat.items.join(', ')}</p>
              </div>
            ))}
            {floatingCategory && floatingCategory.rect && (
              <div
                className="solved-row animate-in"
                style={{
                  backgroundColor: floatingCategory.category.color,
                  position: 'fixed',
                  top: (solvedCategories.length > 0 ? floatingCategory.rect.bottom + 10 : floatingCategory.rect.top) + 'px',
                  left: floatingCategory.rect.left + 'px',
                  width: floatingCategory.rect.width + 'px',
                  zIndex: 10,
                  margin: 0
                }}
              >
                <h3>{floatingCategory.category.title}</h3>
                <p>{floatingCategory.category.items.join(', ')}</p>
              </div>
            )}
          </div>

          <div className="grid">
            {tiles.map((tile) => {
              const isAnimating = animatingIds.includes(tile.id);
              const animIndex = isAnimating
                ? tiles.filter(t => animatingIds.includes(t.id)).findIndex(t => t.id === tile.id)
                : 0;

              let className = `tile ${selectedIds.includes(tile.id) ? 'selected' : ''}`;

              if (isAnimating) {
                if (animationPhase === 'bounce') className += ' bounce';
                if (animationPhase === 'shake') className += ' shake';
                // Fly up is handled via JS styles
              }

              return (
                <div
                  key={tile.id}
                  ref={(el) => {
                    if (el) tileRefs.current.set(tile.id, el);
                    else tileRefs.current.delete(tile.id);
                  }}
                  className={className}
                  style={isAnimating && animationPhase === 'bounce' ? { animationDelay: `${animIndex * 100}ms` } : {}}
                  onClick={() => toggleTile(tile.id)}
                >
                  <FitText>{tile.text}</FitText>
                </div>
              );
            })}
          </div>

          <div className="status-section">
            {feedbackMessage && (
              <p className={`feedback-message ${feedbackExiting ? 'fade-out' : ''}`}>
                {feedbackMessage}
              </p>
            )}
          </div>

          <div className="button-row">
            <button className="game-btn" onClick={handleShuffle}>Shuffle</button>
            <button
              className="game-btn"
              onClick={deselectAll}
              disabled={selectedIds.length === 0}
            >
              Deselect all
            </button>
            <button
              className="game-btn submit"
              disabled={selectedIds.length !== 4}
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>

        </>
      )}

      {isCreatorMode && (
      <div className="creator-section">
        <h2>Create Your Own Puzzle</h2>
        <div className="creator-grid">
          {creatorCategories.map((cat, catIndex) => (
            <div key={catIndex} className="creator-row">
              <input
                className="creator-input title-input"
                style={{ backgroundColor: cat.color }}
                placeholder={`Category ${catIndex + 1} Title`}
                value={cat.title}
                onChange={(e) => handleCreatorChange(catIndex, -1, e.target.value)}
              />
              <div className="creator-items">
                {cat.items.map((item, itemIndex) => (
                  <input
                    key={itemIndex}
                    className="creator-input item-input"
                    style={{ backgroundColor: cat.color }}
                    placeholder={`Tile ${itemIndex + 1}`}
                    value={item}
                    onChange={(e) => handleCreatorChange(catIndex, itemIndex, e.target.value)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <button className="game-btn create-btn" onClick={handleCreateLink}>
          Copy Shareable Link
        </button>

        <div className="generated-link-container">
          <p>Puzzle Code:</p>
          <textarea
            className="generated-link-input"
            readOnly
            value={generatedLink}
            placeholder="Puzzle code will appear here..."
            onClick={(e) => e.currentTarget.select()}
          />
        </div>

        <div className="debug-section">
          <h3>Decoder Tool</h3>
          <div className="debug-row">
            <textarea
              className="debug-input"
              placeholder="Enter puzzle code here..."
              value={debugInput}
              onChange={(e) => handleDebugInputChange(e.target.value)}
            />
            <textarea
              className="debug-input"
              readOnly
              placeholder="Decoded puzzle will appear here."
              value={debugOutput}
            />
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default ConnectionsGame;